import { Auth } from 'aws-amplify';
import { reportCdkRefusal } from 'app/services/session/cdkRefusalSignal';
import { captureException } from '@sentry/react';
import type {
  FileUploadKind,
  V2CreateFileUploadUrlInput,
  V2DeleteFilePayload,
  V2FileDownloadUrlPayload,
  V2FileUploadUrlPayload,
  V2GetFileDownloadUrlInput,
} from 'CDK_API';
import { fetchWithTransportRetry, isNetworkTransportError } from './networkTransport';

export type { FileUploadKind };

export interface DownloadUrlOptions {
  /** Ask the browser to save the file instead of rendering it. */
  download?: boolean;
  /** The name the saved file gets; defaults to the key's basename. */
  fileName?: string;
}

export interface UploadFileArgs {
  kind: FileUploadKind;
  file: Blob;
  /** The user's file name; the server keeps it recognisable but chooses the key. */
  fileName: string;
  /** Defaults to the Blob's type. The upload is signed for exactly this value. */
  contentType?: string;
  /** Required for every kind except PROFILE_PICTURE. */
  paidolId?: string;
  /** TRANSACTION_RECEIPT: the transaction the receipt belongs to. */
  resourceId?: string;
}

export interface UploadedFile {
  /** The key to store on the owning record, without the `public/` root: the same
   *  value `Storage.put(...).key` used to return. */
  key: string;
  /** The random segment of the key. */
  id: string;
}

const GET_FILE_DOWNLOAD_URL = /* GraphQL */ `
  query V2GetFileDownloadUrl($input: V2GetFileDownloadUrlInput!) {
    v2GetFileDownloadUrl(input: $input) {
      url
      key
      expiresAt
    }
  }
`;

const CREATE_FILE_UPLOAD_URL = /* GraphQL */ `
  mutation V2CreateFileUploadUrl($input: V2CreateFileUploadUrlInput!) {
    v2CreateFileUploadUrl(input: $input) {
      url
      key
      id
      contentType
      expiresAt
    }
  }
`;

const DELETE_FILE = /* GraphQL */ `
  mutation V2DeleteFile($input: V2DeleteFileInput!) {
    v2DeleteFile(input: $input) {
      success
      key
    }
  }
`;

// A signed URL is reused until a minute before it expires, so a page that shows
// the same receipt thumbnail or avatar many times asks the backend once.
const URL_REUSE_MARGIN_MS = 60_000;
const URL_CACHE_LIMIT = 500;
// Receipts arrive from phones on weak connections; the transport default is
// sized for JSON round trips, not a 10 MB PUT.
const UPLOAD_TIMEOUT_MS = 120_000;
const DEFAULT_CONTENT_TYPE = 'application/octet-stream';

interface CachedUrl {
  url: string;
  expiresAt: number;
}

/**
 * Customer files (receipts, statements, exports, coding imports, profile
 * pictures) behind a membership check (SEE-1319 / SEE-1575).
 *
 * The Cognito identity pool no longer holds any S3 permission on the storage
 * bucket. Every read, upload and delete asks the CDK API for a short-lived
 * presigned URL; the v2FileAccess Lambda derives the owning company from the key
 * and refuses anyone who is not a member. Keys are unchanged from what
 * `aws-amplify` Storage produced, so the values stored on records stay valid.
 */
export class FileAccessService {
  private readonly cdkAppSyncEndpoint: string;
  private readonly urlCache = new Map<string, CachedUrl>();
  // Bumped by clear(), so a URL minted for the previous session is not cached after it.
  private cacheGeneration = 0;

  constructor() {
    this.cdkAppSyncEndpoint = process.env.REACT_APP_CDK_APPSYNC_ENDPOINT || '';
    if (!this.cdkAppSyncEndpoint) {
      // eslint-disable-next-line no-console
      console.warn('REACT_APP_CDK_APPSYNC_ENDPOINT is not configured');
    }
  }

  /** A presigned GET URL for a stored key. Throws when the caller may not read it. */
  async getDownloadUrl(key: string, options: DownloadUrlOptions = {}): Promise<string> {
    const cacheKey = `${key}|${options.download ? 1 : 0}|${options.fileName ?? ''}`;
    const cached = this.urlCache.get(cacheKey);
    if (cached && cached.expiresAt - URL_REUSE_MARGIN_MS > Date.now()) {
      return cached.url;
    }

    const input: V2GetFileDownloadUrlInput = { key };
    if (options.download) input.download = true;
    if (options.fileName) input.fileName = options.fileName;

    const generation = this.cacheGeneration;
    const data = await this.execute<{ v2GetFileDownloadUrl: V2FileDownloadUrlPayload }>(
      GET_FILE_DOWNLOAD_URL,
      { input },
      'v2GetFileDownloadUrl'
    );
    const payload = data.v2GetFileDownloadUrl;
    if (generation === this.cacheGeneration) {
      this.remember(cacheKey, payload.url, payload.expiresAt);
    }
    return payload.url;
  }

  /** Forgets every cached URL. Called on sign-out, so the next user in this tab mints their own. */
  clear(): void {
    this.cacheGeneration += 1;
    this.urlCache.clear();
  }

  /** Fetches the file as a Blob (statements and the receipt "download" button). */
  async downloadBlob(key: string, options: DownloadUrlOptions = {}): Promise<Blob> {
    const url = await this.getDownloadUrl(key, options);
    const response = await fetchWithTransportRetry(
      url,
      { method: 'GET' },
      { retries: 1, timeoutMs: UPLOAD_TIMEOUT_MS }
    );
    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.status} ${response.statusText}`);
    }
    return response.blob();
  }

  /** Mints an upload URL for a server-chosen key and PUTs the file there. */
  async uploadFile(args: UploadFileArgs): Promise<UploadedFile> {
    const contentType = (args.contentType || args.file.type || DEFAULT_CONTENT_TYPE).toLowerCase();
    const input: V2CreateFileUploadUrlInput = {
      kind: args.kind,
      fileName: args.fileName,
      contentType,
    };
    if (args.paidolId) input.paidolId = args.paidolId;
    if (args.resourceId) input.resourceId = args.resourceId;

    const data = await this.execute<{ v2CreateFileUploadUrl: V2FileUploadUrlPayload }>(
      CREATE_FILE_UPLOAD_URL,
      { input },
      'v2CreateFileUploadUrl'
    );
    const grant = data.v2CreateFileUploadUrl;

    // A fresh key is written once, so re-sending the body after a dropped
    // connection is safe.
    const response = await fetchWithTransportRetry(
      grant.url,
      { method: 'PUT', headers: { 'content-type': grant.contentType }, body: args.file },
      { retries: 2, timeoutMs: UPLOAD_TIMEOUT_MS }
    );
    if (!response.ok) {
      const error = new Error(`Failed to upload file: ${response.status} ${response.statusText}`);
      captureException(error, {
        tags: { area: 'file-access', type: 'upload' },
        extra: { kind: args.kind, key: grant.key },
      });
      throw error;
    }
    return { key: grant.key, id: grant.id };
  }

  /** Removes a receipt file. Only receipt keys of the caller's company are accepted. */
  async deleteFile(key: string): Promise<void> {
    await this.execute<{ v2DeleteFile: V2DeleteFilePayload }>(
      DELETE_FILE,
      { input: { key } },
      'v2DeleteFile'
    );
    for (const cacheKey of Array.from(this.urlCache.keys())) {
      if (cacheKey.startsWith(`${key}|`)) this.urlCache.delete(cacheKey);
    }
  }

  private remember(cacheKey: string, url: string, expiresAt: string): void {
    if (this.urlCache.size >= URL_CACHE_LIMIT) {
      const oldest = this.urlCache.keys().next().value;
      if (oldest !== undefined) this.urlCache.delete(oldest);
    }
    this.urlCache.set(cacheKey, { url, expiresAt: Date.parse(expiresAt) });
  }

  private async execute<T>(query: string, variables: object, operationName: string): Promise<T> {
    if (!this.cdkAppSyncEndpoint) {
      captureException(new Error('CDK AppSync endpoint is not configured'), {
        tags: { area: 'file-access', type: 'config' },
        extra: { envVar: 'REACT_APP_CDK_APPSYNC_ENDPOINT' },
      });
      throw new Error('Service is currently unavailable. Please contact support.');
    }

    try {
      const response = await this.fetchWithAuth(JSON.stringify({ query, variables }));
      let json: { data?: T; errors?: Array<{ message: string; errorType?: string }> };
      try {
        json = await response.json();
      } catch {
        throw new Error(`HTTP ${response.status}: non-JSON response`);
      }
      reportCdkRefusal(json?.errors, { authMode: 'cognito', operationName });
      if (!response.ok) {
        throw new Error(json?.errors?.map((e) => e.message).join(', ') || `HTTP ${response.status}`);
      }
      if (json.errors && json.errors.length > 0) {
        // Keep the `{ errors: [{ message, errorType }] }` shape so callers can tell
        // "Unauthorized" from a bad request, as with the other CDK services.
        throw Object.assign(new Error(json.errors.map((e) => e.message).join(', ')), { errors: json.errors });
      }
      if (!json.data) {
        throw new Error('No data returned from operation');
      }
      return json.data;
    } catch (error) {
      if (error instanceof Error) {
        captureException(error, {
          tags: {
            area: 'file-access',
            type: isNetworkTransportError(error) ? 'network-transport' : 'graphql',
          },
          extra: { operation: operationName, endpoint: this.cdkAppSyncEndpoint },
        });
        throw error;
      }
      throw new Error('An unexpected error occurred. Please try again.');
    }
  }

  private async fetchWithAuth(body: string): Promise<Response> {
    const session = await Auth.currentSession();
    const jwt = session.getIdToken().getJwtToken();
    return fetchWithTransportRetry(
      this.cdkAppSyncEndpoint,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json', Authorization: jwt },
        body,
      },
      // Minting a URL is idempotent, so a dropped connection may be retried.
      { retries: 2 }
    );
  }
}

export const fileAccessService = new FileAccessService();
